import { useState } from 'react'
import { Phone, Mail, Users, MapPin, StickyNote, Cog, Plus, Trash2 } from 'lucide-react'
import { Button, Input, Select, Textarea } from '@/components/ui'
import { formatDate } from '@mavatech/shared'
import { trpc } from '@/lib/trpc'
import { useAppStore } from '@/stores/app.store'

type Meta = { label: string; icon: React.ComponentType<{ className?: string }> }

const TYPE_META: Record<string, Meta> = {
  note: { label: 'Notiz', icon: StickyNote },
  call: { label: 'Anruf', icon: Phone },
  email: { label: 'E-Mail', icon: Mail },
  meeting: { label: 'Termin', icon: Users },
  site_visit: { label: 'Objektbesuch', icon: MapPin },
  system: { label: 'System', icon: Cog },
}
const FALLBACK_META: Meta = { label: 'Notiz', icon: StickyNote }

export function JournalSection({ customerId }: { customerId: string }) {
  const utils = trpc.useUtils()
  const addToast = useAppStore((s) => s.addToast)
  const entries = trpc.customer.journal.useQuery({ customerId })
  const [type, setType] = useState('note')
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10))

  const refresh = () => {
    utils.customer.journal.invalidate({ customerId })
    utils.customer.list.invalidate()
  }
  const add = trpc.customer.addJournalEntry.useMutation({
    onSuccess: () => {
      setTitle('')
      setBody('')
      refresh()
      addToast({ type: 'success', title: 'Eintrag hinzugefügt' })
    },
    onError: (e) => addToast({ type: 'error', title: 'Fehler', description: e.message }),
  })
  const del = trpc.customer.deleteJournalEntry.useMutation({ onSuccess: refresh })

  return (
    <section>
      <h2 className="text-sm font-semibold text-text-primary mb-3">Verlauf</h2>

      <div className="card p-4 mb-4">
        <div className="grid grid-cols-[160px_1fr_150px] gap-3">
          <Select
            label="Art"
            value={type}
            onChange={(e) => setType(e.target.value)}
            options={Object.entries(TYPE_META)
              .filter(([k]) => k !== 'system')
              .map(([k, v]) => ({ value: k, label: v.label }))}
          />
          <Input label="Betreff" value={title} onChange={(e) => setTitle(e.target.value)} />
          <Input label="Datum" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </div>
        <div className="mt-3">
          <Textarea label="Details (optional)" rows={2} value={body} onChange={(e) => setBody(e.target.value)} />
        </div>
        <div className="mt-3 flex justify-end">
          <Button
            variant="primary"
            size="sm"
            loading={add.isPending}
            disabled={!title.trim()}
            onClick={() =>
              add.mutate({
                customerId,
                type: type as 'note' | 'call' | 'email' | 'meeting' | 'site_visit',
                title: title.trim(),
                body: body.trim() || null,
                occurredAt: new Date(date),
              })
            }
          >
            <Plus className="w-3.5 h-3.5" /> Eintrag
          </Button>
        </div>
      </div>

      {entries.isLoading ? (
        <p className="text-sm text-text-tertiary">Lädt …</p>
      ) : !entries.data?.length ? (
        <p className="text-sm text-text-tertiary">Noch keine Einträge.</p>
      ) : (
        <ol className="space-y-3">
          {entries.data.map((e) => {
            const meta = TYPE_META[e.type] ?? FALLBACK_META
            const Icon = meta.icon
            return (
              <li key={e.id} className="flex gap-3">
                <div className="w-7 h-7 rounded-md bg-bg-active flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Icon className="w-3.5 h-3.5 text-text-tertiary" />
                </div>
                <div className="flex-1 min-w-0 card p-3">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-medium text-text-primary">{e.title}</p>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-xs text-text-tertiary">
                        {meta.label} · {formatDate(e.occurredAt as unknown as string)}
                      </span>
                      {e.type !== 'system' && (
                        <button
                          className="text-text-tertiary hover:text-danger"
                          onClick={() => del.mutate({ id: e.id, customerId })}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                  {e.body && <p className="text-sm text-text-secondary mt-1 whitespace-pre-wrap">{e.body}</p>}
                </div>
              </li>
            )
          })}
        </ol>
      )}
    </section>
  )
}
